import React, { useRef, useState } from "react";
import AuthedImage from "@/components/AuthedImage";

const parsePos = (p) => {
  const [x, y] = (p || "50% 50%").split(" ").map((s) => parseFloat(s));
  return { x: isNaN(x) ? 50 : x, y: isNaN(y) ? 50 : y };
};

export default function HeroEditor({ photoId, fit, pos, onChange }) {
  const ref = useRef(null);
  const start = useRef(null);
  const [dragging, setDragging] = useState(false);

  const btn = (active) =>
    `text-xs px-3 py-1.5 rounded-full transition-colors ${active ? "bg-[#3A5A40] text-[#F9F6F0]" : "bg-[#F0EBE1] text-[#5C5C5C] hover:bg-[#E5E0D8]"}`;

  const onDown = (e) => {
    if (fit !== "cover") return;
    const el = ref.current;
    const { x, y } = parsePos(pos);
    start.current = { mx: e.clientX, my: e.clientY, x, y, w: el.clientWidth, h: el.clientHeight };
    setDragging(true);
    try { el.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  };
  const onMove = (e) => {
    if (!dragging || !start.current) return;
    const s = start.current;
    const dxp = ((e.clientX - s.mx) / s.w) * 100;
    const dyp = ((e.clientY - s.my) / s.h) * 100;
    const nx = Math.min(100, Math.max(0, Math.round(s.x - dxp)));
    const ny = Math.min(100, Math.max(0, Math.round(s.y - dyp)));
    onChange({ fit, pos: `${nx}% ${ny}%` });
  };
  const onUp = (e) => {
    setDragging(false);
    start.current = null;
    try { ref.current.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
  };

  return (
    <div data-testid="hero-editor">
      <div className="flex items-center gap-2 mb-3">
        <button type="button" onClick={() => onChange({ fit: "contain", pos })} className={btn(fit === "contain")} data-testid="hero-fit-contain">
          Fit — show whole photo
        </button>
        <button type="button" onClick={() => onChange({ fit: "cover", pos })} className={btn(fit === "cover")} data-testid="hero-fit-cover">
          Fill — crop to banner
        </button>
      </div>
      <div
        ref={ref}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        className={`relative h-44 overflow-hidden bg-[#1F1F1F] select-none touch-none ${fit === "cover" ? "cursor-move" : ""}`}
        data-testid="hero-editor-preview"
      >
        <AuthedImage photoId={photoId} variant="hero" fit={fit} position={pos} />
        {fit === "cover" && !dragging && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-xs text-white bg-black/40 px-2 py-1 rounded">drag to reposition</span>
          </div>
        )}
      </div>
      <p className="text-[11px] text-[#8A8A8A] mt-1">
        {fit === "cover"
          ? "Fills the banner edge-to-edge — drag the image to choose what shows."
          : "Shows the entire photo with a soft blurred background (never cropped)."}
      </p>
    </div>
  );
}
